import { config } from '../config';
import { TokenError } from '../exceptions/TokenError';
import { CardConfig, IGatewayConfig, IProvider } from '../types';

const Z2PAY_CDN_URL = config.API_ENDPOINT?.includes('homolog')
  ? 'https://cdn.homolog.z2pay.com'
  : 'https://cdn.z2pay.com';

let scriptLoaded = false;
let scriptLoading: Promise<void> | null = null;

function waitForZ2Pay(timeout = 5000): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    if ((window as any).Z2Pay?.Tokenizer) return resolve();

    const start = Date.now();
    const interval = setInterval(() => {
      if ((window as any).Z2Pay?.Tokenizer) {
        clearInterval(interval);
        resolve();
      } else if (Date.now() - start > timeout) {
        clearInterval(interval);
        reject(new Error(`Z2Pay.Tokenizer not available after ${timeout}ms`));
      }
    }, 50);
  });
}

function loadScript(): Promise<void> {
  if (scriptLoaded) return Promise.resolve();
  if (scriptLoading) return scriptLoading;

  scriptLoading = new Promise<void>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = `${Z2PAY_CDN_URL}/assets/tokenizer.js`;
    script.async = true;

    script.onload = () => {
      scriptLoaded = true;
      waitForZ2Pay().then(resolve).catch(reject);
    };

    script.onerror = () => reject(new Error('Failed to load Z2Pay tokenizer script'));
    document.head.appendChild(script);
  });

  return scriptLoading;
}

export class Z2Pay implements IProvider {
  private companyId: string;

  constructor(
    private options: IGatewayConfig[],
    private gatewayId: string,
    private isTest?: boolean
  ) {
    this.companyId = this.options.find((c) => c.name === 'public_key')?.value ?? '';
  }

  public async generate(config: CardConfig) {
    try {
      await loadScript();

      const Z2PayTokenizer = (window as any).Z2Pay.Tokenizer;

      if (!Z2PayTokenizer) {
        throw new Error('Z2Pay tokenizer not found on window');
      }

      const tokenizer = new Z2PayTokenizer({ companyId: this.companyId });

      const result = await tokenizer.createCardToken({
        number: config.number,
        holder: config.holder,
        document: config.document,
        cvv: config.cvv,
        expiration: config.expiration,
      });

      return result.tokenId;
    } catch (err: any) {
      const message = err?.message ?? 'Unknown error';
      console.error('[Z2Pay Tokenizer] Failed to generate token:', message, err);
      throw new TokenError(`Failed to generate token: ${message}`);
    }
  }
}
